import React, { useState, useEffect } from 'react';
import { Search, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function PeriodoNotas({ filters }) {
    const [estudiantes, setEstudiantes] = useState([]);
    const [notasProceso, setNotasProceso] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (filters.id_asignatura) {
            loadEstudiantes();
            loadNotasProceso();
        }
    }, [filters.id_asignatura, filters.id_paralelo, filters.gestion, filters.periodo]);

    const loadEstudiantes = async () => {
        try {
            setLoading(true);
            const query = new URLSearchParams({
                id_asignatura: filters.id_asignatura,
                ...(filters.id_paralelo && { id_paralelo: filters.id_paralelo })
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/docente/estudiantes?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setEstudiantes(data.estudiantes || []);
        } catch (error) {
            console.error('Error loading estudiantes:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadNotasProceso = async () => {
        try {
            const query = new URLSearchParams({
                id_asignatura: filters.id_asignatura,
                ...(filters.id_paralelo && { id_paralelo: filters.id_paralelo }),
                gestion: filters.gestion,
                periodo: filters.periodo
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/docente/notas-proceso?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            // Organizar por estudiante y calcular promedios mensuales
            const notasMap = {};
            (data.notas || []).forEach(n => {
                if (!notasMap[n.id_estudiante]) {
                    notasMap[n.id_estudiante] = {};
                }
                notasMap[n.id_estudiante][n.semana] = n.nota;
            });
            setNotasProceso(notasMap);
        } catch (error) {
            console.error('Error loading notas proceso:', error);
        }
    };

    const calcularMes = (id_estudiante, mes) => {
        const notasEst = notasProceso[id_estudiante] || {};
        // Mes 1: semanas 1-4, Mes 2: semanas 5-8, Mes 3: semanas 9-12
        const semanasInicio = (mes - 1) * 4 + 1;
        const semanasFin = mes * 4;

        const notasMes = [];
        for (let s = semanasInicio; s <= semanasFin; s++) {
            if (notasEst[s] !== undefined && notasEst[s] !== null) {
                notasMes.push(notasEst[s]);
            }
        }

        if (notasMes.length === 0) return '-';
        const promedio = notasMes.reduce((sum, n) => sum + n, 0) / notasMes.length;
        return Math.round(promedio);
    };

    const filteredEstudiantes = estudiantes.filter(est =>
        searchTerm === '' ||
        est.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        est.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-4">
            {/* Barra de herramientas */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800">Notas de Periodo</h3>
                    <p className="text-sm text-gray-600">Periodo {filters.periodo} - Gestión {filters.gestion}</p>
                    <p className="text-xs text-gray-500">Promedio de 4 semanas por mes</p>
                </div>

                <div className="flex items-center gap-2">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Buscar estudiante..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    </div>

                    <button
                        onClick={() => alert('Descarga funcionalidad')}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                        <Download size={18} />
                        Descargar
                    </button>
                </div>
            </div>

            {/* Tabla */}
            {loading ? (
                <div className="p-8 text-center text-gray-500">Cargando...</div>
            ) : filteredEstudiantes.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                    {filters.id_asignatura ? 'No hay estudiantes' : 'Selecciona una asignatura'}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b">
                                <th className="p-3 text-left text-sm font-semibold text-gray-700">No</th>
                                <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                                <th className="p-3 text-left text-sm font-semibold text-gray-700">Email</th>
                                <th className="p-3 text-center text-sm font-semibold text-gray-700 bg-blue-50">Mes 1</th>
                                <th className="p-3 text-center text-sm font-semibold text-gray-700 bg-green-50">Mes 2</th>
                                <th className="p-3 text-center text-sm font-semibold text-gray-700 bg-yellow-50">Mes 3</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEstudiantes.map((est, idx) => {
                                const mes1 = calcularMes(est.id_estudiante, 1);
                                const mes2 = calcularMes(est.id_estudiante, 2);
                                const mes3 = calcularMes(est.id_estudiante, 3);

                                return (
                                    <tr key={est.id_estudiante} className="border-b hover:bg-gray-50">
                                        <td className="p-3 text-sm">{idx + 1}</td>
                                        <td className="p-3 text-sm font-medium">{est.codigo}</td>
                                        <td className="p-3 text-sm">{est.email}</td>
                                        <td className="p-3 text-center bg-blue-50">
                                            <span className="font-semibold text-blue-700">{mes1}</span>
                                        </td>
                                        <td className="p-3 text-center bg-green-50">
                                            <span className="font-semibold text-green-700">{mes2}</span>
                                        </td>
                                        <td className="p-3 text-center bg-yellow-50">
                                            <span className="font-semibold text-yellow-700">{mes3}</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
